import { useMemo } from 'react';

import { useMcpConnectors } from './use-mcp-connectors';

export interface IntegrationsFeatureStatus {
  isPaused: boolean;
  activeConnectorCount: number;
  lastUpdated: string | null;
}

/**
 * Sprint A4 S1 PR 3 — deriva o estado da feature (paused vs active) a partir
 * dos conectores listados.
 *
 * Regra:
 *   - Algum conector com `status='paused'` → isPaused=true (feature foi pausada
 *     globalmente pelo admin; backend marca todos os conectores com pausedAt).
 *   - Senão → isPaused=false. activeConnectorCount conta status='active'.
 *
 * Nota: estado canonical real vive em `CapabilityEntry` no backend; este hook
 * deriva via convenção do schema (todos paused juntos = feature pausada).
 * Cross-validado com WS event `integrations.feature-toggled` no Task 16.
 */
export function useIntegrationsFeatureStatus(): IntegrationsFeatureStatus & {
  isLoading: boolean;
} {
  const { data, isLoading } = useMcpConnectors();

  const result = useMemo<IntegrationsFeatureStatus>(() => {
    if (data === undefined) {
      return { isPaused: false, activeConnectorCount: 0, lastUpdated: null };
    }
    const hasPaused = data.some((c) => c.status === 'paused');
    const activeCount = data.filter((c) => c.status === 'active').length;
    const lastUpdated =
      data
        .map((c) => c.updatedAt)
        .sort()
        .reverse()[0] ?? null;
    return {
      isPaused: hasPaused,
      activeConnectorCount: activeCount,
      lastUpdated,
    };
  }, [data]);

  return { ...result, isLoading };
}
